import * as React from "react"
import { cn } from "@/lib/utils"
import { Check, Circle, Loader2 } from "lucide-react"

export interface Step {
  id: string
  label: string
  description?: string
  status?: "pending" | "active" | "completed" | "error"
  icon?: React.ReactNode
}

interface StepperProps extends React.HTMLAttributes<HTMLDivElement> {
  steps: Step[]
  currentStep: number
  orientation?: "horizontal" | "vertical"
  size?: "sm" | "md" | "lg"
  showSpinner?: boolean
}

const sizeStyles = {
  sm: {
    circle: "w-6 h-6",
    icon: "w-3 h-3",
    text: "text-xs",
  },
  md: {
    circle: "w-8 h-8",
    icon: "w-4 h-4",
    text: "text-sm",
  },
  lg: {
    circle: "w-10 h-10",
    icon: "w-5 h-5",
    text: "text-base",
  },
}

const Stepper = React.forwardRef<HTMLDivElement, StepperProps>(
  ({ className, steps, currentStep, orientation = "horizontal", size = "md", showSpinner = false, ...props }, ref) => {
    const styles = sizeStyles[size]

    return (
      <div
        ref={ref}
        className={cn(
          "flex",
          orientation === "horizontal" ? "flex-row items-center" : "flex-col items-start gap-4",
          className
        )}
        {...props}
      >
        {steps.map((step, index) => {
          const isCompleted = index < currentStep
          const isActive = index === currentStep
          const isError = step.status === "error"

          return (
            <React.Fragment key={step.id}>
              <div className={cn("flex items-center", orientation === "vertical" ? "w-full" : "")}>
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      "rounded-full flex items-center justify-center border-2 transition-all duration-300",
                      styles.circle,
                      isCompleted
                        ? "bg-green-600 border-green-600 text-white"
                        : isActive
                        ? "bg-blue-600 border-blue-600 text-white"
                        : isError
                        ? "bg-red-100 border-red-500 text-red-600"
                        : "bg-white border-gray-300 text-gray-400"
                    )}
                  >
                    {isCompleted ? (
                      <Check className={styles.icon} />
                    ) : isActive && showSpinner && step.status !== "error" ? (
                      <Loader2 className={cn(styles.icon, "animate-spin")} />
                    ) : step.icon ? (
                      step.icon
                    ) : (
                      <Circle className={cn(styles.icon, "fill-current")} />
                    )}
                  </div>
                  {orientation === "vertical" && (
                    <div className="mt-2 text-center">
                      <p className={cn("font-medium", styles.text, isActive ? "text-gray-900" : "text-gray-500")}>
                        {step.label}
                      </p>
                      {step.description && (
                        <p className="text-xs text-gray-400 mt-0.5">{step.description}</p>
                      )}
                    </div>
                  )}
                </div>
                {orientation === "horizontal" && (
                  <div className="ml-3">
                    <p className={cn("font-medium", styles.text, isActive ? "text-gray-900" : "text-gray-500")}>
                      {step.label}
                    </p>
                    {step.description && (
                      <p className="text-xs text-gray-400 mt-0.5">{step.description}</p>
                    )}
                  </div>
                )}
              </div>
              {orientation === "horizontal" && index < steps.length - 1 && (
                <div
                  className={cn(
                    "flex-1 h-0.5 mx-4 transition-all duration-300",
                    isCompleted ? "bg-green-600" : "bg-gray-200"
                  )}
                />
              )}
              {orientation === "vertical" && index < steps.length - 1 && (
                <div
                  className={cn(
                    "w-0.5 ml-4 transition-all duration-300",
                    isCompleted ? "bg-green-600" : "bg-gray-200"
                  )}
                  style={{ height: "24px" }}
                />
              )}
            </React.Fragment>
          )
        })}
      </div>
    )
  }
)
Stepper.displayName = "Stepper"

export { Stepper }
